import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  RendererFactory2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { Brand, Category, CustomSearchResults, SearchHistoryItem, Sts, Tap } from '@search/const';
import { CUSTOM_ICON_TYPE } from '@shared/models';
// Импортировано напрямую осознано, так как сторибук не хочет работать
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SearchBarService } from 'libs/search/feature/src/lib/services/search-bar.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'sd-searchbox-search-bar',
  templateUrl: './searchbox-search-bar.component.html',
  styleUrls: ['./searchbox-search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdSearchboxSearchBarComponent implements AfterViewInit, OnDestroy {
  iconTypes = CUSTOM_ICON_TYPE;

  @Input() searchInputControl: FormControl;
  @Input() isLoading: boolean;
  @Input() isEmpty: boolean;
  @Input() results: CustomSearchResults;
  @Input() maxLength: number;
  @Input() query: string;

  @Output() clear = new EventEmitter<void>();
  /* eslint-disable @angular-eslint/no-output-on-prefix */
  @Output() onFocusSearchInput = new EventEmitter<void>();
  @Output() onEnterSearchInput = new EventEmitter<void>();
  @Output() searchHistoryReset = new EventEmitter<void>();
  @Output() searchHistoryRemove = new EventEmitter<string>();
  @Output() onClickTapItem = new EventEmitter<Tap>();
  @Output() onClickHistoryItem = new EventEmitter<SearchHistoryItem>();
  @Output() onClickStsItem = new EventEmitter<Sts>();
  @Output() onClickCategoryItem = new EventEmitter<Category>();
  @Output() onClickBrandItem = new EventEmitter<Brand>();
  /* eslint-enable */

  @ViewChild('searchInput', { read: ElementRef }) searchInputRef: ElementRef;
  @ViewChildren('searchResultItem', { read: ElementRef }) searchResultItems: QueryList<ElementRef>;

  isOpened$ = new BehaviorSubject<boolean>(false);

  private renderer: Renderer2;
  private subscription = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private searchBarService: SearchBarService,
    private hostRef: ElementRef,
    private router: Router,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.searchBarService.getOpenEvents().subscribe(() => this.searchInputRef.nativeElement.focus()),
    );

    this.subscription.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationStart && !(<NavigationStart>event).url.match(/^\w+\/search/)),
        )
        .subscribe(() => {
          if (!this.isEmpty) {
            this.close();
          }
        }),
    );
  }

  /**
   * Закрывать выпадающий список с результатами поиска
   * при нажатии вне хост контейнера
   */
  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: Event): void {
    if (!this.hostRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.toggleSearchboxBodyClass(false);
  }

  open(): void {
    this.toggleSearchboxBodyClass(true);
    this.isOpened$.next(true);
  }

  close(): void {
    this.toggleSearchboxBodyClass(false);
    this.isOpened$.next(false);
  }

  /**
   * Фокус следующего HTML элемента в результатах поиска
   */
  focusNextChild(event: UIEvent): void {
    event.preventDefault(); // Negate normal keyscroll
    const [results, focusedIndex] = [this.getResultElements(), this.getFocusedIndex()];
    // Focus on first index moving to last
    if (results.length) {
      if (focusedIndex >= results.length - 1) {
        results[0].focus();
      } else {
        results[focusedIndex + 1].focus();
      }
    }
  }

  /**
   * Фокус предыдущего HTML элемента в результатах поиска
   */
  focusPreviousChild(event: UIEvent): void {
    event.preventDefault(); // Negate normal keyscroll
    const [results, focusedIndex] = [this.getResultElements(), this.getFocusedIndex()];
    // Focus on last index moving to first
    if (results.length) {
      if (focusedIndex < 1) {
        results[results.length - 1].focus();
      } else {
        results[focusedIndex - 1].focus();
      }
    }
  }

  /**
   * Получить сфокусированный HTML элемент из результатов поиска
   */
  private getFocusedIndex(): number {
    return this.getResultElements().indexOf(<HTMLElement>this.document.activeElement);
  }

  /**
   * Получить список всех HTML элементов из результатов поиска
   */
  private getResultElements(): HTMLElement[] {
    return this.searchResultItems.map((ref) => ref.nativeElement);
  }

  private toggleSearchboxBodyClass(isOpened: boolean): void {
    if (isOpened) {
      this.renderer.addClass(this.document.body, 'searchbox-is-active');
    } else {
      this.renderer.removeClass(this.document.body, 'searchbox-is-active');
    }
  }
}
